/**
 * ============================================================================
 * useActivatePolicyMutation — Phase 6a frontend (T-P6a-W3-01)
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: POST /api/v1/retention/policies/{id}/activate — activate a PENDING
 *          policy.
 *
 * REGULATORY:
 *   Activation is GMP_CRITICAL — requires TWO distinct signature bundle ids
 *   (RETENTION_POLICY_DUAL_SIGN_A / _B). The server enforces "distinct signer"
 *   and "PENDING-only" preconditions; the UI just forwards the two bundle ids.
 *   This mirrors the tenant-config dual-sign pattern already in production.
 * ============================================================================
 */

import { useMutation, type UseMutationResult } from '@tanstack/react-query';
import { apiFetch, newIdempotencyKey } from '../../api/client';
import type { RetentionPolicy } from './usePoliciesQuery';

export interface ActivatePolicyInput {
  policyId: string;
  signatureBundleIdA: string;
  signatureBundleIdB: string;
}

export function useActivatePolicyMutation(): UseMutationResult<
  RetentionPolicy,
  Error,
  ActivatePolicyInput
> {
  return useMutation<RetentionPolicy, Error, ActivatePolicyInput>({
    mutationFn: ({ policyId, signatureBundleIdA, signatureBundleIdB }) =>
      apiFetch<RetentionPolicy>(
        `/api/v1/retention/policies/${encodeURIComponent(policyId)}/activate`,
        {
          method: 'POST',
          body: {
            signature_bundle_ids: [signatureBundleIdA, signatureBundleIdB],
          },
          idempotencyKey: newIdempotencyKey(),
        },
      ),
  });
}
